import { useEffect, useMemo, useRef, useState } from 'react'
import type { Balance, LeaveTypeId } from '@lms/shared-types'
import { Icon } from '../primitives/Icon'
import { AutoFilledTag } from '../primitives/AutoFilledTag'
import { Tooltip } from '../primitives/Tooltip'
import { LEAVE_TYPE_STYLE } from '../../lib/leave-type-palette'
import {
  LEAVE_TYPE_DESCRIPTION,
  LEAVE_TYPE_DOC_REQUIRED,
  LEAVE_TYPE_LABEL,
} from '../../lib/leave-type-meta'
import type { LeaveTypeMetadata } from '../../api/leave-types'
import { isApplicable } from '../../lib/applicable-genders'
import { isLeaveTypeExhausted } from '../../lib/leave-type-meta'
import { flags } from '../../lib/feature-flags'
import { useReducedMotion } from '../../lib/use-reduced-motion'

/** Full picker order — mirrors `CollapsedTypeCard.TYPE_GRID_ORDER`. `unpaid`
 *  is intentionally NOT selectable: it engages automatically via the overflow
 *  flow when a credit type runs out of balance (RFC 004 §4.3). */
const TYPE_ORDER: ReadonlyArray<LeaveTypeId> = [
  'annual',
  'medical',
  'hospitalization',
  'compassionate',
  'marriage',
  'birthday',
  'replacement',
  'maternity',
  'paternity',
]

/** Policy §1.3 fallback — types hidden during probation when the
 *  /leave-types endpoint provides no metadata to derive them from. */
const PROBATION_RESTRICTED_FALLBACK: ReadonlySet<LeaveTypeId> = new Set<LeaveTypeId>([
  'birthday',
  'marriage',
])

export interface LeaveTypePickerProps {
  /** Empty string renders the unselected placeholder ("Select leave type").
   *  The form ships with no default type, so the trigger must handle a blank
   *  value. `onChange` still only ever emits a concrete type. */
  value: LeaveTypeId | ''
  onChange: (next: LeaveTypeId) => void
  balances: ReadonlyArray<Balance>
  /** "F" hides paternity; "M" hides maternity. "X"/null → show both. */
  gender: 'M' | 'F' | 'X' | null | undefined
  /** Hides probation-restricted types so the employee never hits a wall. */
  onProbation?: boolean | undefined
  /** Server leave-type metadata — read for `probationRestricted`. */
  leaveTypesData?: ReadonlyArray<LeaveTypeMetadata> | undefined
  /** When true, surfaces an inline AutoFilledTag next to the "Leave type" label. */
  autoFilled?: boolean
  /** Leave types the caller has determined can't be applied for right now
   *  (e.g. Birthday once the birth month has passed). Hidden from the menu,
   *  like exhausted types — except the currently-selected value. */
  unavailableTypes?: ReadonlySet<LeaveTypeId>
}

/**
 * Smart-intake leave-type picker — the Lumen redesign's single-line dropdown
 * that replaces `CollapsedTypeCard`'s tile grid. Trigger shows a color dot +
 * "{Type} leave" + a `PROOF REQUIRED` pill for document-required types + a
 * caret that rotates on open. The menu lists every selectable type (with the
 * same gender/probation gating as the grid) with its dot, a faint `PROOF`
 * tag, and a check on the selected row. Keyboard: ↑/↓ move, Enter selects,
 * Escape + click-out close.
 */
export function LeaveTypePicker({
  value,
  onChange,
  balances,
  gender,
  onProbation,
  leaveTypesData,
  autoFilled = false,
  unavailableTypes,
}: LeaveTypePickerProps): JSX.Element {
  const [open, setOpen] = useState<boolean>(false)
  const [activeIndex, setActiveIndex] = useState<number>(0)
  const ref = useRef<HTMLDivElement | null>(null)
  const reduced = useReducedMotion()

  // Lookup table — each dropdown row reads its own balance to render the
  // "N days" label. Building it once per render keeps the option map
  // pure and avoids a per-row O(n) find().
  const balanceByType = useMemo(() => {
    const map = new Map<LeaveTypeId, Balance>()
    for (const b of balances) map.set(b.leaveType, b)
    return map
  }, [balances])

  const probationRestrictedSet = useMemo<ReadonlySet<LeaveTypeId>>(() => {
    if (!onProbation) return new Set()
    const fromData = (leaveTypesData ?? [])
      .filter((lt) => lt.probationRestricted)
      .map((lt) => lt.id)
    return fromData.length > 0 ? new Set(fromData) : PROBATION_RESTRICTED_FALLBACK
  }, [onProbation, leaveTypesData])

  const visibleTypes = useMemo<ReadonlyArray<LeaveTypeId>>(() => {
    let filtered = TYPE_ORDER as ReadonlyArray<LeaveTypeId>
    if (gender != null) filtered = filtered.filter((id) => isApplicable(id, gender))
    if (probationRestrictedSet.size > 0) {
      filtered = filtered.filter((id) => !probationRestrictedSet.has(id))
    }
    // Exhausted and no-balance types are hidden from the selector.
    //   * No balance row → treated as a hard zero and hidden: there is nothing
    //     to draw on, and the server blocks the application too (leave_rules
    //     R9b). Applies to every balance-bearing type including Annual.
    //   * A present row at 0 is hidden per `isLeaveTypeExhausted` (which keeps
    //     Annual visible while it can still accrue).
    // Currently-selected value is exempt — defensive pattern mirrors the
    // gender/probation filters above — so the trigger never loses what
    // the user already picked (e.g. a ?type= deep-link stays selectable until
    // the user actively changes it).
    filtered = filtered.filter((id) => {
      if (id === value) return true
      // Caller-declared unavailable (e.g. Birthday after its month passes).
      if (unavailableTypes?.has(id)) return false
      const b = balanceByType.get(id)
      if (b === undefined) return false
      // Structural eligibility (flag on): a type the employee is not yet
      // eligible for (tenure/probation, independent of balance) is hidden
      // entirely from the picker — it surfaces as "NOT YET ELIGIBLE" on the
      // balance tile instead. Server also filters GET /leave-types the same way.
      if (flags.leaveEligibilityBadge && b.eligible === false) return false
      return !isLeaveTypeExhausted(id, b.available)
    })
    return filtered
  }, [gender, probationRestrictedSet, balanceByType, value, unavailableTypes])

  // Close on click-out + Escape; keep the active row synced to the selection
  // when the menu opens.
  useEffect(() => {
    if (!open) return
    setActiveIndex(Math.max(0, value ? visibleTypes.indexOf(value) : -1))
    const onPointer = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    return () => document.removeEventListener('mousedown', onPointer)
  }, [open, value, visibleTypes])

  // `value` is '' before the employee picks a type — guard the per-type
  // lookups so the trigger can fall back to the "Select leave type"
  // placeholder instead of dereferencing an undefined style entry.
  const def = value ? LEAVE_TYPE_STYLE[value] : null
  const needsProof = value ? LEAVE_TYPE_DOC_REQUIRED[value] : false

  const select = (id: LeaveTypeId): void => {
    onChange(id)
    setOpen(false)
  }

  const onTriggerKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      setOpen(true)
    }
  }

  const onMenuKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => (i + 1) % visibleTypes.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => (i - 1 + visibleTypes.length) % visibleTypes.length)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const id = visibleTypes[activeIndex]
      if (id) select(id)
    }
  }

  return (
    <div data-testid="collapsed-type-card">
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: 'var(--text-2)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 8,
        }}
      >
        <span>Leave type</span>
        <span
          aria-label="required"
          style={{
            color: 'var(--danger)',
            textTransform: 'none',
            letterSpacing: 0,
            fontWeight: 600,
          }}
        >
          *
        </span>
        {autoFilled ? <AutoFilledTag /> : null}
        {/* Info affordance — only once a type is selected. Sits at the far
             right of the label row (marginLeft:auto). A small bell tinted to
             the selected leave type's own colour that periodically swings, so
             the eye catches the motion without a loud fixed colour. Hover/focus
             surfaces the type's policy description. */}
        {value && LEAVE_TYPE_DESCRIPTION[value] ? (
          <span
            data-testid="leave-type-description-info"
            style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center' }}
          >
            <Tooltip content={LEAVE_TYPE_DESCRIPTION[value] as string} placement="top-end">
              <button
                type="button"
                data-testid="leave-type-description-trigger"
                aria-label={`About ${LEAVE_TYPE_LABEL[value]} leave`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 24,
                  height: 24,
                  padding: 0,
                  border: 'none',
                  background: 'transparent',
                  color: LEAVE_TYPE_STYLE[value].swatch,
                  cursor: 'pointer',
                  // Resting tilt so the bell reads as "ringing"; the swing
                  // animation on the inner span oscillates around this angle.
                  transform: 'rotate(15deg)',
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    display: 'inline-flex',
                    transformOrigin: 'top center',
                    animation: reduced ? 'none' : 'lms-bell-swing 3.2s ease-in-out infinite',
                  }}
                >
                  <Icon
                    name="notifications"
                    size={20}
                    weight={500}
                    color="currentColor"
                    aria-hidden="true"
                  />
                </span>
              </button>
            </Tooltip>
          </span>
        ) : null}
      </div>

      <div ref={ref} style={{ position: 'relative' }}>
        <button
          type="button"
          // Test ID matches the legacy CollapsedTypeCard "Change" affordance —
          // existing ApplyLeavePage tests query `type-change-btn` to expand
          // the picker. Kept stable here so the redesign is a transparent
          // replacement at the test layer.
          data-testid="type-change-btn"
          onClick={() => setOpen((o) => !o)}
          onKeyDown={onTriggerKeyDown}
          aria-haspopup="listbox"
          aria-expanded={open}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '11px 14px',
            minHeight: 44,
            background: 'var(--surface)',
            border: `1px solid ${open ? 'var(--steel-blue)' : 'var(--border-strong)'}`,
            boxShadow: open ? '0 0 0 3px var(--baby-blue-tint)' : 'none',
            borderRadius: 10,
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: 13.5,
            color: 'var(--text)',
            textAlign: 'left',
            transition: reduced ? 'none' : 'border-color .12s, box-shadow .12s',
          }}
        >
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: def ? def.swatch : 'var(--border-strong)',
              flex: 'none',
              boxShadow: def
                ? `0 0 0 3px color-mix(in srgb, ${def.swatch} 13%, transparent)`
                : 'none',
            }}
          />
          {def ? (
            <span style={{ fontWeight: 500 }}>{def.label} leave</span>
          ) : (
            <span style={{ fontWeight: 500, color: 'var(--text-3)' }}>Select leave type</span>
          )}
          {needsProof ? (
            <span
              style={{
                fontSize: 9.5,
                fontFamily: 'var(--font-mono)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: 'var(--text-2)',
                fontWeight: 700,
                padding: '2px 7px',
                borderRadius: 999,
                background: 'var(--surface-2)',
                border: '1px solid var(--border)',
              }}
            >
              Proof required
            </span>
          ) : null}
          {/* Balance for the currently-selected type — mirrors the row
               labels in the menu so the at-a-glance answer
               ("how many days do I have left?") stays visible after the
               employee closes the dropdown. */}
          {value ? (
            // Right-align the balance so it sits next to the caret rather than
            // floating in the middle of the trigger after the type label.
            <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center' }}>
              <BalanceLabel
                balance={balanceByType.get(value)}
                leaveTypeId={value}
              />
            </span>
          ) : null}
          <span
            style={{
              // With a value the balance owns the auto margin, so the caret
              // just needs the flex gap; on the placeholder the caret pushes
              // itself to the right edge.
              marginLeft: value ? 0 : 'auto',
              color: 'var(--text-3)',
              display: 'inline-flex',
              alignItems: 'center',
              transition: reduced ? 'none' : 'transform .15s',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <Icon name="expand_more" size={16} aria-hidden="true" />
          </span>
        </button>

        {open ? (
          <div
            role="listbox"
            data-testid="leave-type-grid"
            tabIndex={-1}
            aria-activedescendant={`lt-opt-${visibleTypes[activeIndex] ?? ''}`}
            onKeyDown={onMenuKeyDown}
            ref={(el) => {
              el?.focus()
            }}
            style={{
              position: 'absolute',
              top: 'calc(100% + 6px)',
              left: 0,
              right: 0,
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 12,
              boxShadow:
                '0 18px 40px -18px rgba(32,41,69,.28), 0 6px 16px -8px rgba(32,41,69,.12)',
              padding: 4,
              zIndex: 20,
              maxHeight: 360,
              overflowY: 'auto',
              outline: 'none',
            }}
          >
            {visibleTypes.map((id, i) => {
              const d = LEAVE_TYPE_STYLE[id]
              const selected = value === id
              const active = i === activeIndex
              const proofy = LEAVE_TYPE_DOC_REQUIRED[id]
              return (
                <button
                  key={id}
                  id={`lt-opt-${id}`}
                  role="option"
                  aria-selected={selected}
                  // Legacy alias — ApplyLeavePage tests iterate
                  // `getAllByTestId('leave-type-pill')` and filter by label,
                  // matching the prior LeaveTypePill grid contract.
                  data-testid="leave-type-pill"
                  data-leave-type={id}
                  onClick={() => select(id)}
                  onMouseEnter={() => setActiveIndex(i)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '9px 11px',
                    minHeight: 40,
                    borderRadius: 8,
                    background: selected
                      ? 'var(--baby-blue-tint)'
                      : active
                        ? 'var(--surface-2)'
                        : 'transparent',
                    border: 'none',
                    fontFamily: 'inherit',
                    fontSize: 13,
                    color: 'var(--text)',
                    fontWeight: selected ? 600 : 500,
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: reduced ? 'none' : 'background .1s',
                  }}
                >
                  <span
                    style={{
                      width: 9,
                      height: 9,
                      borderRadius: '50%',
                      background: d.swatch,
                      flex: 'none',
                      boxShadow: `0 0 0 2.5px color-mix(in srgb, ${d.swatch} 13%, transparent)`,
                    }}
                  />
                  <span>{LEAVE_TYPE_LABEL[id]}</span>
                  {proofy ? (
                    <span
                      style={{
                        fontSize: 9,
                        fontFamily: 'var(--font-mono)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        color: 'var(--text-3)',
                        fontWeight: 700,
                        marginLeft: 4,
                      }}
                    >
                      Proof
                    </span>
                  ) : null}
                  {/* Remaining balance per type, right-aligned. Lets the
                       employee compare available days across types without
                       leaving the dropdown. Hidden when no balance row
                       exists for the type (e.g. unpaid never appears here
                       anyway; replacement may have zero before any grant). */}
                  <BalanceLabel
                    balance={balanceByType.get(id)}
                    leaveTypeId={id}
                  />
                  {selected ? (
                    <span
                      style={{
                        marginLeft: 4,
                        color: 'var(--steel-blue)',
                        display: 'inline-flex',
                      }}
                    >
                      <Icon name="check" size={14} aria-hidden="true" />
                    </span>
                  ) : null}
                </button>
              )
            })}
          </div>
        ) : null}
      </div>
    </div>
  )
}

/** Right-aligned "N days" balance label for a dropdown row / trigger. Reads
 *  the balance row for the type and renders the available count, tinted danger
 *  once it hits 0.
 *
 *  No cascade hint: the old "unpaid" / "uses annual" suffixes described the
 *  annual→unpaid overflow (and medical→annual fallback), both removed on
 *  2026-07-21. Balances are hard-capped now, so an exhausted type simply reads
 *  "0 days" — filing beyond it means an explicit unpaid request, not a silent
 *  spill. Hidden when no balance row exists (e.g. replacement before a grant,
 *  and unpaid — which has no balance row at all). */
function BalanceLabel({
  balance,
  leaveTypeId,
}: {
  balance: Balance | undefined
  leaveTypeId: LeaveTypeId
}): JSX.Element | null {
  if (balance === undefined) return null
  // Unpaid has no entitlement — any seeded balance row is an artifact, and a
  // "300 days" label would wrongly imply a quota. Never show a count for it.
  if (leaveTypeId === 'unpaid') return null
  // Never surface a negative balance ("-13.5 days") — an over-drawn balance
  // reads as 0 left. Clamp the displayed number at 0.
  const displayDays = Math.max(0, balance.available)
  const formatted = Number.isInteger(displayDays)
    ? String(displayDays)
    : displayDays.toFixed(1).replace(/\.0$/, '')
  const exhausted = balance.available <= 0

  return (
    <span
      data-testid={`leave-type-option-${leaveTypeId}-balance`}
      style={{
        marginLeft: 'auto',
        fontSize: 11.5,
        fontFamily: 'var(--font-mono)',
        color: exhausted ? 'var(--danger)' : 'var(--text-2)',
        whiteSpace: 'nowrap',
        flex: 'none',
      }}
    >
      {formatted} {displayDays === 1 ? 'day' : 'days'}
    </span>
  )
}
